/**
 * Phase 8 §15 I — Global keyboard shortcuts sheet.
 * Opens on `?` key (when no text input is focused).
 */
import { useEffect } from 'react';
import { X } from 'lucide-react';

interface ShortcutRow {
  keys: string[];
  description: string;
}

const SECTIONS: { title: string; rows: ShortcutRow[] }[] = [
  {
    title: 'Navigation',
    rows: [
      { keys: ['⌘', 'K'], description: 'Open command palette (new search)' },
      { keys: ['⌘', '/'], description: 'Open command palette (find)' },
      { keys: ['Esc'], description: 'Close modal / cancel action (top overlay only)' },
      { keys: ['?'], description: 'Open this shortcuts sheet' },
    ],
  },
  {
    title: 'Assistant',
    rows: [
      { keys: ['⌘', '⇧', 'A'], description: 'Toggle Assistant slide-over (any route)' },
      { keys: ['⌘', '⇧', '/'], description: 'Focus Assistant composer (opens if closed)' },
    ],
  },
  {
    title: 'Smart Match',
    rows: [
      { keys: ['j'], description: 'Select next candidate row' },
      { keys: ['k'], description: 'Select previous candidate row' },
      { keys: ['s'], description: 'Shortlist selected candidate' },
      { keys: ['h'], description: 'Hide / reject selected candidate' },
      { keys: ['Enter'], description: 'Open detail panel for selected row' },
    ],
  },
  {
    title: 'General',
    rows: [
      { keys: ['↑', '↓'], description: 'Move through list items' },
      { keys: ['Enter'], description: 'Confirm / open selected item' },
    ],
  },
];

interface Props {
  open: boolean;
  onClose: () => void;
}

export function KeyboardShortcutsModal({ open, onClose }: Props) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (open) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[400] flex items-center justify-center p-4 bg-black/50 backdrop-blur-md"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Keyboard shortcuts"
        className="relative w-full max-w-lg rounded-[14px] border border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-elevated)] shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center justify-between px-5 py-3.5 border-b border-[var(--vscode-border-primary)]"
          style={{ backgroundColor: 'var(--brand-popup-header)' }}
        >
          <h2 className="text-sm font-semibold text-white">Keyboard shortcuts</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-white/70 transition-colors hover:bg-white/15 hover:text-white"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto">
          {SECTIONS.map((section) => (
            <div key={section.title}>
              <p className="text-[11px] font-semibold uppercase tracking-wide vscode-text-tertiary mb-2">
                {section.title}
              </p>
              <div className="space-y-1">
                {section.rows.map((row) => (
                  <div
                    key={row.description}
                    className="flex items-center justify-between gap-4 py-1"
                  >
                    <span className="text-[13px] vscode-text-secondary">{row.description}</span>
                    <div className="flex items-center gap-1 shrink-0">
                      {row.keys.map((k) => (
                        <kbd
                          key={k}
                          className="inline-flex min-w-[22px] items-center justify-center rounded-[6px] border border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-secondary)] px-1.5 py-0.5 text-[11px] font-mono font-medium vscode-text-primary"
                        >
                          {k}
                        </kbd>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
